import type { RouteLocationNormalized } from 'vue-router'
import type { Composer } from 'vue-i18n'

export interface BreadcrumbItem {
  title: string
  to?: string | { name: string }
  disabled?: boolean
  icon?: string
}

/**
 * Route configuration for breadcrumb generation
 */
const routeBreadcrumbConfig: Record<string, { 
  label: string
  icon?: string
  parent?: string
}> = {
  'home': {
    label: 'common.home',
    icon: 'mdi-home'
  },
  'login': {
    label: 'auth.login',
    icon: 'mdi-login'
  },
  'register': {
    label: 'register.title',
    icon: 'mdi-account-plus',
    parent: 'home'
  },
  'profile': {
    label: 'profile.title',
    icon: 'mdi-account',
    parent: 'home'
  },
  'users': {
    label: 'users.title',
    icon: 'mdi-account-group',
    parent: 'home'
  },
  'admin-config': {
    label: 'admin.config.title',
    icon: 'mdi-cog',
    parent: 'home'
  },
  'setup': {
    label: 'setup.title',
    icon: 'mdi-cog'
  },
  'email-verification': {
    label: 'emailVerification.title',
    icon: 'mdi-email-check',
    parent: 'home'
  },
  'email-verification-form': {
    label: 'emailVerification.title',
    icon: 'mdi-email-check',
    parent: 'home'
  },
  'email-change-verification': {
    label: 'emailVerification.title',
    icon: 'mdi-email-check',
    parent: 'profile'
  },
  'not-found': {
    label: 'errors.notFound.title',
    icon: 'mdi-alert-circle',
    parent: 'home'
  },
  'games': {
    label: 'games.title',
    icon: 'mdi-gamepad-variant',
    parent: 'home'
  },
  'gameshows': {
    label: 'gameshows.title',
    icon: 'mdi-television-play',
    parent: 'home'
  },
  'gameshow-detail': {
    label: 'gameshow.details.title',
    icon: 'mdi-eye',
    parent: 'gameshows'
  }
}

/**
 * Generates breadcrumbs for the current route
 */
export function generateBreadcrumbs(
  route: RouteLocationNormalized,
  t: Composer['t']
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = []
  const routeName = route.name as string

  if (!routeName || !routeBreadcrumbConfig[routeName]) {
    return breadcrumbs
  }

  // Build breadcrumb chain by following parent relationships
  const buildChain = (name: string): string[] => {
    const config = routeBreadcrumbConfig[name]
    if (!config) return []
    
    const chain = [name]
    if (config.parent && config.parent !== name) {
      chain.unshift(...buildChain(config.parent))
    }
    return chain
  }

  const chain = buildChain(routeName)

  // Convert chain to breadcrumb items
  chain.forEach((name, index) => {
    const config = routeBreadcrumbConfig[name]
    const isLast = index === chain.length - 1

    // Handle dynamic titles for specific routes
    let title = t(config.label)
    if (name === 'gameshow-detail' && route.meta?.gameShowName) {
      title = route.meta.gameShowName as string
    }

    breadcrumbs.push({
      title,
      to: isLast ? undefined : { name },
      disabled: isLast,
      icon: config.icon
    })
  })

  return breadcrumbs
}

/**
 * Gets the page title for the current route
 */
export function getPageTitle(
  route: RouteLocationNormalized,
  t: Composer['t']
): string {
  const routeName = route.name as string
  const config = routeBreadcrumbConfig[routeName]
  
  if (!config) {
    return t('app.title')
  }
  
  return t(config.label)
}